import { Dialog } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewEncapsulation,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import {
  filter,
  mergeMap,
  of,
  switchMap,
  tap
} from 'rxjs';

import { MediaQueryService } from '@app/@core/breakpoints';
import { AuthState } from '@app/modules/users';
import { BrowserApiService, RouteData } from '@core/common';
import { PreloadRectComponent } from '@core/common/directives';
import { indicateLoading } from '@core/helpers';
import { Notifier } from '@core/notifications';
import { LoginDialog } from '@ui/dialogs';

import { catchCaptcha } from '../../../captcha';
import { PROMOCODE_APPLIED_MESSAGE, SECRET_CODE_APPLIED_MESSAGE } from '../../../payment/components/user-bonus-secret-code/user-bonus-secret-code.component';
import { UserCodeTypes } from '../../../payment/user-bonus.entity';
import { UserBonusState } from '../../../payment/user-bonus.state';
import { UserConditionsDialog } from '../../../promocodes/dialogs/user-conditions/user-conditions.dialog';
import { PromocodesState } from '../../../promocodes/promocodes.state';
import { DialogData, ServiceItem, ServiceItemOpenResult } from '../../../service-items/service-items.entity';
import { getServiceItemDialog } from '../../../service-items/utils';
import { MainService } from '../../main.service';
import { MainState } from '../../main.state';
import BonusWheelBannerComponent from '../banners/bonus-wheel/bonus-wheel.component';
import BonusesBannerComponent from '../banners/bonuses/banner/bonuses.component';
import DepositBannerComponent from '../banners/deposit-banner/deposit-banner.component';
import MiniGamesBannerComponent from '../banners/mini-games/mini-games.component';
import QuizBannerComponent from '../banners/quiz-banner/quiz-banner.component';
import RafflesBannerComponent from '../banners/raffles/banner/banner.component';
import SupportBannerComponent from '../banners/support-banner/support-banner.component';
import UpgradeBannerComponent from '../banners/upgrade/upgrade.component';
import VipClubBannerComponent from '../banners/vip-club/vip-club.component';
import { LiveDropComponent } from '../live-drop/live-drop.component';
import { SliderBannersComponent } from '../slider-banners/slider-banners.component';

const SUCCESS_COPY_PROMO_CODE = $localize`:@@main.promocode.success_copy:Код скопирован!`;

@Component({
  selector: 'main-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  providers: [MainState, MainService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'main-page'
  },
  encapsulation: ViewEncapsulation.None,
  imports: [
    SliderBannersComponent,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    LiveDropComponent,
    PreloadRectComponent,
    BonusWheelBannerComponent,
    VipClubBannerComponent,
    UpgradeBannerComponent,
    MiniGamesBannerComponent,
    RafflesBannerComponent,
    DepositBannerComponent,
    SupportBannerComponent,
    BonusesBannerComponent,
    QuizBannerComponent,
    NgTemplateOutlet
  ]
})
export class HomeComponent {
  public static ROUTE_DATA: RouteData = {
    meta: {
      title: $localize`:@@meta.sitename:CSFAIL — Лидер среди CS2/CS:GO игровых сайтов`,
      description: $localize`:@@meta.sitename.description:CSFAIL предлагает непревзойденный опыт Crash, Jackpot, Wheel, 
      Double, Crazy Wheel CS:GO/CS2. Ежедневные раздачи, промокоды. Пополнение картами, скинами, usdt, ton, btc.`,
      rawTitle: true
    }
  };

  public readonly mainState = inject(MainState);
  public readonly authState = inject(AuthState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  public readonly promocodesState = inject(PromocodesState);
  public readonly userBonusState = inject(UserBonusState);
  private readonly notifier = inject(Notifier);
  private readonly router = inject(Router);

  public readonly isServer = inject(BrowserApiService).isServer();
  public readonly preloadLiveDrops = new Array(6);
  public readonly isApplying = signal<boolean>(false);
  public readonly isSuccessApplied = signal<boolean>(false);
  public userInputCodeControl = new FormControl('', [Validators.required]);
  private readonly mediaService = inject(MediaQueryService);
  public isLg = toSignal(this.mediaService.mediaQuery('max', 'lg'));

  constructor() {
    this.loadData();
  }

  usePromocode(): void {
    const promocode = this.userInputCodeControl.value?.trim() || '';

    if (!this.authState.isAuthenticated()) {
      sessionStorage.setItem('pendingPromocode', promocode);
      this.dialog.open(LoginDialog);

      return;
    }

    this.applyPromocode(promocode);
  }

  private checkPromocode(): void {
    const pendingPromocode = sessionStorage.getItem('pendingPromocode');

    if (pendingPromocode) {
      sessionStorage.removeItem('pendingPromocode');
      this.applyPromocode(pendingPromocode);
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart && event.url !== '/auth/oauth'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.checkPromocode());

    this.dialog.afterAllClosed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.checkPromocode();
    });
  }

  private applyPromocode(promocode: string): void {
    this.isSuccessApplied.set(false);
    this.promocodesState
      .usePromocode(promocode)
      .pipe(
        indicateLoading(this.isApplying),
        catchCaptcha(this.dialog, () => this.applyPromocode(promocode)),
        mergeMap((data) => {
          if (data.type === UserCodeTypes.SECRET_CODE) {
            return of(data).pipe(
              tap(() => {
                this.isSuccessApplied.set(true);
                this.notifier.success(SECRET_CODE_APPLIED_MESSAGE);
                this.router.navigate(['/payment']);
              }),
              switchMap(() => this.userBonusState.loadUserBonuses())
            );
          }

          return of(data).pipe(
            tap(() => {
              if (data.type === UserCodeTypes.PROMOCODE) {
                if (data?.result?.useId) {
                  this.isSuccessApplied.set(true);
                  this.notifier.success(PROMOCODE_APPLIED_MESSAGE);
                  this.router.navigate(['/payment']);
                } else {
                  this.dialog.open(UserConditionsDialog);
                }

                const serviseItem = data.result?.use?.[0]?.serviceItem?.item || data.result?.serviceItem;
                const serviceResult =
                  data.result?.use?.[0]?.serviceItem?.useResult || data.result?.serviceItemUseResult;

                if (serviseItem && serviceResult) {
                  this.showServiceItemDialog(serviseItem, serviceResult);
                }
              }
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async showServiceItemDialog(serviceItem: ServiceItem, openResult: ServiceItemOpenResult): Promise<void> {
    const { type } = serviceItem;

    const dialogComponent = getServiceItemDialog(type);

    if (!dialogComponent) {
      return;
    }

    const dialogData: DialogData = {
      openResult,
      serviceItem,
      inventoryItem: undefined,
      isHiddenButton: true,
      callback: () => {}
    };

    const cmp = await dialogComponent();

    this.dialog.open(cmp.default, {
      data: dialogData
    });
  }

  private loadData(): void {
    this.mainState.fetchLiveDrops().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    if (!this.isServer) {
      this.mainState.fetchBanners().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }
}
